import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'umi';
import { Select } from 'tea-component';
import { EntryProps } from '../interface';
import { checkNullString } from '@/pages/datalab/utils/utils';
import { FilterStringOptions } from '@dbl/core/filter';
import { OptionPageSize } from '@/pages/datalab/components/SortableDrag/configration';
import { getColunmOptions } from '@dbl/services/global';
import type { IOptionType } from '@dbl/core/dataSource';
import type { TResponseError } from '@dbl/services/common';
import StringSelectorWithTag from '@/pages/datalab/components/StringSelectorWithTag';
import {
  useStringSelectorProps,
} from '@/pages/datalab/components/StringSelectorWithTag/hooks';
import { uniq } from 'lodash-es';

const OPERATION_INCLUDE = FilterStringOptions[0].value;

export const FilterNewStringEntry = (props: EntryProps) => {
  const { column, onChange, isEn, customOptionLoader } = props;
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state.datalab?.gameCode,
  }));

  const [total, setTotal] = useState<number>(0);

  const operationValue = useMemo(() => {
    if (checkNullString(column.operation)) {
      return OPERATION_INCLUDE;
    }
    return column.operation ?? OPERATION_INCLUDE;
  }, [column.operation]);

  const operationOptions = useMemo(
    () =>
      FilterStringOptions.map((opt) => ({
        value: opt.value,
        text: isEn ? (opt.text_en ?? opt.text) : opt.text,
        text_en: opt.text_en ?? opt.text,
      })),
    [isEn],
  );

  const params = useMemo(
    () => ({
      game_code: gameCode || '',
      table_id: column.table_id,
      dim_name: column.column_name,
      page_no: 1,
      page_size: OptionPageSize,
    }),
    [gameCode, column.table_id, column.column_name],
  );

  const requestOptions = useCallback(
    async (p: typeof params & { keyword?: string; }) => {
      try {
        if (customOptionLoader) {
          const list: IOptionType[] = await customOptionLoader(
            column,
            p.game_code || gameCode || '',
          );
          setTotal(list.length);
          return {
            value_list: list?.map((o) => ({
              value: o?.value,
              display: o?.text ?? o?.value,
              display_en: o?.text_en ?? o?.text ?? o?.value,
            })),
            total_size: list.length,
          };
        }
        const res: any = await getColunmOptions({
          table_id: p.table_id,
          game_code: p.game_code || '',
          dim_name: p.dim_name,
          page_no: p.page_no ?? 1,
          page_size: p.page_size ?? OptionPageSize,
          query_type: 0,
          dimension_setting: column.dimension_setting,
          keyword: p.keyword,
        });
        const totalSize =
          res?.total_size ?? res?.value_list?.length ?? 0;
        setTotal(totalSize);
        return {
          value_list: res?.value_list ?? [],
          total_size: totalSize,
        };
      } catch (e) {
        setTotal(0);
        throw e;
      }
    },
    [column, gameCode, customOptionLoader],
  );

  const {
    loading,
    error,
    options,
    originalOptions,
    onSearchKeywordChange,
    isClearSearchWord,
  } = useStringSelectorProps({
    requestOptions,
    params,
    thenCallback: () => ({ allData: true }),
  });

  // 根据 isEn 生成带中英文的展示用 options，下拉列表按当前语言显示 text/title(搜索过滤后的options)
  const displayOptions = useMemo(
    () =>
      options?.map((o) => ({
        ...o,
        text: isEn ? (o.text_en ?? o.text) : o.text,
        title: isEn ? (o.text_en ?? o.title ?? o.text) : (o.title ?? o.text),
      })),
    [options, isEn],
  );

  // 原始options列表，未搜索过滤（用于翻译）
  const allDisplayOptions = useMemo(
    () =>
      originalOptions?.map((o) => ({
        ...o,
        text: isEn ? (o.text_en ?? o.text) : o.text,
        title: isEn ? (o.text_en ?? o.title ?? o.text) : (o.title ?? o.text),
      })),
    [originalOptions, isEn],
  );

  // 根据 originalOptions 生成唯一值列表，用于判断全选状态(原始options列表，未搜索过滤)
  const uniqOptionsValues = useMemo(() => {
    return uniq(originalOptions?.map((o) => o.value ?? ''));
  }, [originalOptions]);

  const normalizedError = useMemo((): TResponseError | undefined => {
    if (!error) return undefined;
    const err = error as Error & { code?: number | string; msg?: string; };
    return {
      code: err.code ?? -1,
      msg: err.msg ?? (err as Error).message,
    };
  }, [error]);

  // 全选状态
  const isAllState = useMemo(() => {
    const values = column.column_value ?? [];
    const optionsAll = column.options_all ?? [];
    if (values.length === 0) {
      return (column.isAll as boolean) ?? false;
    }
    // 兼容旧数据：column_value 与 options_all 长度相同视为全选
    if (optionsAll.length > 0 && values.length === optionsAll.length) {
      return true;
    }
    if (total > 0 && values.length >= total) {
      return true;
    }
    return (column.isAll as boolean) ?? false;
  }, [column.column_value, column.isAll, column.options_all, total]);

  // 全选时传完整 options 用于下拉勾选展示；持久化仍为 column_value=[] + isAll；兼容旧数据用 column_value 或 options_all
  const displayValues = useMemo(() => {
    if (!isAllState) {
      return column.column_value ?? [];
    }
    if (options.length > 0) {
      return options.map((o) => o.value ?? '');
    }
    // 旧数据或 options 未加载：用 column_value（旧数据全选时为完整列表）或 options_all 的 value 列表
    const values = column.column_value ?? [];
    const optionsAll = column.options_all ?? [];
    return values.length >= (optionsAll.length || total) ? values : optionsAll?.map((o) => o.value ?? '');
  }, [isAllState, options, column.column_value, column.options_all, total]);

  const handleCheckChange = useCallback(
    (values: string[], _isFull: boolean, _customValues: string[], context?: any) => {
      // 仅当来自 StringSelectorWithTag 的 useEffect 同步（context===true）且已是「空数组+isAll」时跳过，避免栈溢出
      // !! 一定要return，否则会死循环
      const isSyncFromEffect = context === true;
      if (isSyncFromEffect) return;
      // 操作逻辑：全选/非全选时 column_value 统一传空数组，由 isAll 区分全选与否；部分选时传实际 values
      // 如果 values 与 originalOptions 的唯一值数量相同，则认为是全选（fix：后端可能返回code相同的数据，导致长度不一致）
      const isAll = uniq(values)?.length === uniqOptionsValues?.length;
      onChange(operationValue, isAll ? [] : values, undefined, { isAll });
    },
    [operationValue, uniqOptionsValues, onChange],
  );

  const handleOperationChange = useCallback(
    (value: string) => {
      if (value === column.operation) {
        return;
      }
      onChange(value, column.column_value || []);
    },
    [column, onChange],
  );

  return (
    <>
      <Select
        className="fs-entry-string-operation"
        appearance="button"
        options={operationOptions}
        value={operationValue}
        onChange={handleOperationChange}
      />
      <StringSelectorWithTag
        className='fs-entry-string-values'
        name={isEn ? column.column_display_en : column.column_display}
        options={displayOptions}
        allOptions={allDisplayOptions}
        loading={loading}
        error={normalizedError}
        operation={operationValue as 'include' | 'exclude'}
        onOperationChange={(value) => handleOperationChange(value)}
        isExclude={operationValue === 'exclude'}
        isAllState={isAllState}
        isClearSearchWord={isClearSearchWord}
        total={total}
        showOperationRadio={false}
        values={displayValues}
        onCheckChange={handleCheckChange}
        onSearchKeywordChange={onSearchKeywordChange}
        searchState={false}
      />
    </>
  );
};
